import { useParams, Link } from 'react-router-dom';
import { articles } from '../../mocks/articles';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';

export default function ArticleDetail() {
  const { id } = useParams<{ id: string }>();
  const article = articles.find(a => a.id === id);

  if (!article) {
    return (
      <div className="min-h-screen bg-white">
        <Navbar />
        <div className="max-w-4xl mx-auto px-6 py-20 text-center">
          <h1 className="text-3xl font-bold text-slate-900 mb-4">Article Not Found</h1>
          <Link to="/articles" className="text-teal-600 hover:text-teal-700 font-medium">
            ← Back to Articles
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  const relatedArticles = articles
    .filter(a => a.id !== article.id && a.category === article.category)
    .slice(0, 3);

  if (relatedArticles.length < 3) {
    const additionalArticles = articles
      .filter(a => a.id !== article.id && a.category !== article.category)
      .slice(0, 3 - relatedArticles.length);
    relatedArticles.push(...additionalArticles);
  }

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      {/* Hero Image */}
      <div className="relative w-full h-[500px] bg-slate-900">
        <img 
          src={article.coverImage} 
          alt={article.title}
          className="w-full h-full object-cover object-top opacity-90"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/20 to-black/60"></div>
      </div>

      {/* Article Content */}
      <article className="max-w-4xl mx-auto px-6 -mt-32 relative z-10">
        {/* Article Header */}
        <div className="bg-white rounded-lg shadow-xl p-8 md:p-12 mb-12">
          <Link 
            to="/articles" 
            className="inline-flex items-center text-teal-600 hover:text-teal-700 font-medium mb-6 transition-colors"
          >
            <i className="ri-arrow-left-line mr-2"></i>
            Back to Articles
          </Link>

          <div className="mb-6">
            <span className="inline-block px-4 py-1.5 bg-teal-100 text-teal-700 text-sm font-semibold rounded-full">
              {article.category}
            </span>
          </div>

          <h1 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6 font-['Playfair_Display']">
            {article.title}
          </h1>

          <div className="flex items-center gap-6 text-slate-600 text-sm border-t border-b border-slate-200 py-4">
            <div className="flex items-center gap-2">
              <i className="ri-user-line"></i>
              <span className="font-medium">{article.author}</span>
            </div>
            <div className="flex items-center gap-2">
              <i className="ri-calendar-line"></i>
              <span>{new Date(article.publishDate).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</span>
            </div>
            <div className="flex items-center gap-2">
              <i className="ri-time-line"></i>
              <span>{article.readTime}</span>
            </div>
          </div>
        </div>

        {/* Article Body */}
        <div className="bg-white rounded-lg shadow-lg p-8 md:p-12 mb-12">
          <div className="prose prose-lg max-w-none">
            <p className="text-xl text-slate-700 leading-relaxed mb-8 font-medium">
              {article.excerpt}
            </p>

            <h2 className="text-3xl font-bold text-slate-900 mt-12 mb-6 font-['Playfair_Display']">
              The Challenge of Modern Business Leadership
            </h2>
            <p className="text-slate-700 leading-relaxed mb-6">
              In today's rapidly evolving business landscape, organizations face unprecedented challenges that demand specialized expertise across multiple domains. Traditional consulting models often fall short, providing recommendations without the hands-on execution needed to drive real transformation. This is where the fractional leadership model becomes a game-changer.
            </p>
            <p className="text-slate-700 leading-relaxed mb-6">
              By embedding experienced leaders directly into your organization, you gain access to world-class expertise without the overhead of full-time executive salaries. These professionals don't just advise—they roll up their sleeves and work alongside your team, building capabilities that last long after their engagement ends.
            </p>

            <h2 className="text-3xl font-bold text-slate-900 mt-12 mb-6 font-['Playfair_Display']">
              A Different Approach to Growth
            </h2>
            <p className="text-slate-700 leading-relaxed mb-6">
              What sets fractional leadership apart is the integration model. Rather than maintaining distance as external consultants, fractional leaders become part of your team. They attend your meetings, understand your culture, and work within your systems. This deep integration enables them to identify opportunities and implement solutions that truly fit your organization.
            </p>
            <p className="text-slate-700 leading-relaxed mb-6">
              The methodology focuses on three core principles: hands-on execution, knowledge transfer, and sustainable capability building. Every engagement is designed not just to solve immediate challenges, but to equip your team with the skills and frameworks to continue driving growth independently.
            </p>

            <h2 className="text-3xl font-bold text-slate-900 mt-12 mb-6 font-['Playfair_Display']">
              Real Results, Lasting Impact
            </h2>
            <p className="text-slate-700 leading-relaxed mb-6">
              Organizations that embrace fractional leadership see measurable improvements across key metrics. Revenue growth accelerates as sales organizations become more effective. Brand positioning strengthens as marketing strategies align with business objectives. Customer experiences improve as CX leaders optimize every touchpoint.
            </p>
            <p className="text-slate-700 leading-relaxed mb-6">
              But perhaps most importantly, these improvements persist. Because fractional leaders work from within, they transfer knowledge and build internal capabilities. Your team doesn't just benefit from their expertise during the engagement—they learn to think and operate at a higher level permanently.
            </p>

            <h2 className="text-3xl font-bold text-slate-900 mt-12 mb-6 font-['Playfair_Display']">
              The Path Forward
            </h2>
            <p className="text-slate-700 leading-relaxed mb-6">
              As businesses continue to navigate complexity and change, the need for flexible, embedded expertise will only grow. The fractional leadership model represents a fundamental shift in how organizations access and leverage top-tier talent. It's not about hiring consultants to tell you what to do—it's about bringing in leaders who work alongside you to make it happen.
            </p>
            <p className="text-slate-700 leading-relaxed mb-6">
              Whether you're looking to transform your sales organization, elevate your brand, optimize customer experiences, or drive commerce growth, the right fractional leader can accelerate your journey while building lasting capabilities within your team. The question isn't whether you need this expertise—it's how quickly you can integrate it into your organization.
            </p>
          </div>
        </div>

        {/* Related Articles */}
        {relatedArticles.length > 0 && (
          <div className="mb-20">
            <h2 className="text-3xl font-bold text-slate-900 mb-8 font-['Playfair_Display']">
              Related Articles
            </h2>
            <div className="grid md:grid-cols-3 gap-8">
              {relatedArticles.map((relatedArticle) => (
                <Link 
                  key={relatedArticle.id}
                  to={`/articles/${relatedArticle.id}`}
                  className="group bg-white rounded-lg shadow-md overflow-hidden hover:shadow-xl transition-all duration-300"
                >
                  <div className="relative w-full h-48 overflow-hidden">
                    <img 
                      src={relatedArticle.coverImage} 
                      alt={relatedArticle.title}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
                    />
                  </div>
                  <div className="p-6">
                    <span className="inline-block px-3 py-1 bg-teal-100 text-teal-700 text-xs font-semibold rounded-full mb-3">
                      {relatedArticle.category}
                    </span>
                    <h3 className="text-xl font-bold text-slate-900 mb-2 group-hover:text-teal-600 transition-colors font-['Playfair_Display']">
                      {relatedArticle.title}
                    </h3>
                    <p className="text-slate-600 text-sm mb-4 line-clamp-2">
                      {relatedArticle.excerpt}
                    </p>
                    <div className="flex items-center justify-between text-xs text-slate-500">
                      <span>{relatedArticle.readTime}</span>
                      <span className="text-teal-600 font-medium group-hover:text-teal-700">
                        Read More →
                      </span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}
      </article>

      <Footer />
    </div>
  );
}